/**
 * Recent Activity 패널 — 계정 전체의 최근 실행 피드.
 *
 * 픽커 우측 패널(구 SideInfoPanel)에서 랜딩으로 이관하며 독립 컴포넌트로 분리
 * (2026-08-11, 최상위 랜딩 개편). 계정의 시뮬레이션 이력을 BFF에서 최신순으로
 * 한 번에 가져와(ListSimulationHistoriesByUser) 보여준다.
 *
 * 이전에는 "최근 수정된 프로젝트 5개"의 이력만 모았는데, 프로젝트의 updated_at은
 * 저장·편집 때만 바뀌므로 편집한 적 없는 프로젝트에서 돌린 최신 실행이 피드에서
 * 빠지는 문제가 있었다(2026-08-19 스테이지 실측: 당일 실행 대신 6일 전 실행만 표시).
 * 실행 시각 기준으로 계정 전체를 보는 것이 "최근 활동"의 뜻에 맞다.
 */

import { simulationResultPath, simulationRunPath } from '@/app/routes';
import React, { useEffect, useMemo, useState } from 'react';
import { useAsyncScope } from '@/shared/async/useAsyncScope';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Chip,
  Skeleton,
} from '@mui/material';
import { listSimulationHistoriesByUserPage } from '@/app/services/pm/projectManagerService';
import { isHiddenLegacyProject } from '@/app/projectVisibility';
import type { ProjectSummary } from '@/types/project';
import { entryIsLive } from '@/types/project';
import type { SimulationEntry } from '@/types/project';
import { formatRelative } from '@/shared/dateTime';
import { runEntryName } from '@/shared/runName';
import { resolveRunProject, runProjectLabel, runProjectNames } from '@/features/simulation/list/runListModel';
import { RunStatusIcon } from '@/shared/ui/RunStatusIcon';
import { runStatusLabel } from '@/shared/ui/runStatusView';
import { RunHistoryNotice } from '@/shared/ui/RunHistoryNotice';
import { RUN_HISTORY_EMPTY_TEXT, runHistoryView } from '@/shared/ui/runHistoryNoticeView';

/** 표시할 항목 수 / 한 번에 가져올 수(운전 템플릿 실행을 걸러내도 표시 수가 차도록 여유) */
const FEED_ENTRY_LIMIT = 4;
const FEED_FETCH_LIMIT = 12;

interface ActivityEntry extends SimulationEntry {
  projectId: string;
  projectName: string;
}

/** ISO 시각 → "방금 전 / n분 전 / n시간 전 / n일 전 / 날짜" */
interface RecentActivityPanelProps {
  /** 로그인 사용자 id — 계정 전체 이력 조회 키. 없으면(미인증) 빈 피드 */
  userId?: string;
  /** 프로젝트 목록 — 항목의 프로젝트 이름 표시와 운전 템플릿 실행 제외 판정에 쓴다 */
  /** 목록 요약이면 충분하다 — 이름·템플릿 마커만 읽는다 */
  projects: ProjectSummary[];
  /** 프로젝트 목록 로딩 중 — 피드도 스켈레톤 유지 (빈 목록을 "기록 없음"으로 오인 방지) */
  loading?: boolean;
  /** 프로젝트 목록 조회 실패 — 빈 계정("기록 없음")으로 위장하지 않게 실패 문구를 띄운다 */
  loadFailed?: boolean;
}

const RecentActivityPanel: React.FC<RecentActivityPanelProps> = ({
  userId,
  projects,
  loading = false,
  loadFailed = false,
}) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityEntry[] | null>(null); // null = 로딩 중
  const [feedError, setFeedError] = useState(false); // 이력 조회 실패

  // 프로젝트 id → 이름, 운전 템플릿 여부. 운전 시뮬레이터(토이 플랜트) 템플릿의
  // 실행은 별도 운전 HMI의 활동이라 플랫폼 피드에서 제외한다
  // (2026-08-13 사용자 피드백). 판정은 이름+템플릿 마커 — 이름만 보면 동명의
  // 사용자 프로젝트 활동까지 숨긴다(Copilot R2).
  const projectIndex = useMemo(() => {
    // 이름표는 실행 목록과 같은 함수 — 행 없는 프로젝트(퀵런)도 이름을 안다(«삭제된 프로젝트» 로 읽지 않게)
    const names = runProjectNames(projects);
    const operationIds = new Set<string>();
    for (const p of projects) {
      if (isHiddenLegacyProject(p)) operationIds.add(p.id);
    }
    return { names, operationIds };
  }, [projects]);

  const scope = useAsyncScope(userId ?? null);
  useEffect(() => {
    // 토큰은 분기 전에 시작 — 로딩·미인증 분기가 패널을 비운 뒤 이전 조회의 늦은 응답이 다시 채우지 않게(R9)
    const token = scope.lane('feed').begin();
    // 프로젝트 목록이 아직 로딩 중이면 activities 를 확정하지 않는다 — 빈 배열로
    // 확정해 두면 목록 도착~이력 조회 사이에 "기록 없음"이 플래시로 보인다.
    // 직전 조회의 실패 표시가 새 로딩·미인증 상태로 새지 않게 함께 비운다
    // (로그아웃·세션 만료 뒤 빈 피드가 실패 문구로 보이던 경로 — Copilot R1).
    if (loading) {
      setFeedError(false);
      setActivities(null);
      return;
    }
    if (!userId) {
      setFeedError(false);
      setActivities([]);
      return;
    }
    (async () => {
      try {
        const { entries } = await listSimulationHistoriesByUserPage(userId, FEED_FETCH_LIMIT);
        if (!token.isLatest()) return;
        setFeedError(false);
        setActivities(
          entries
            .filter(
              (e): e is SimulationEntry & { projectId: string } =>
                typeof e.projectId === 'string' &&
                e.projectId !== '' &&
                !projectIndex.operationIds.has(e.projectId),
            )
            .map((e) => ({
              ...e,
              // 소속 라벨은 실행 목록과 같은 해석기 — 지워진 프로젝트의 실행은 이력 스냅샷 이름으로 '삭제된 프로젝트 · 이름'
              projectName: runProjectLabel(resolveRunProject(e.projectId, e.projectName, projectIndex.names, true)!),
            }))
            .slice(0, FEED_ENTRY_LIMIT),
        );
      } catch {
        if (!token.isLatest()) return;
        // BFF 미가용 — "기록 없음"과 구분한다. 앞서 읽은 피드는 지킨다(정본 판정이 «새로 불러오지 못했다» 로 말한다)
        setFeedError(true);
      }
    })();
  }, [userId, projectIndex, loading, scope]);

  const handleActivityClick = (entry: ActivityEntry) => {
    if (entryIsLive(entry)) {
      // 살아 있는 행(준비 중·실행 중·일시정지 — entryIsLive)도 기록의 모델 귀속을 싣는다 — 종결 뒤 결과 보기와 같은 문맥(단독 기록은 단독 화면)
      navigate(simulationRunPath(entry.projectId, entry));
      return;
    }
    const simulationId = entry.simId || entry.id;
    navigate(simulationResultPath(entry.projectId, simulationId, 'history', entry));
  };

  // 조회 중(프로젝트 목록·피드)은 뼈대가 말한다 — 알림은 실패만(빈 피드의 실패는 «불러오지 못했다», 앞서 읽은 피드가 있으면 «새로 불러오지 못했다»).
  // 다시 읽는 중에는 지난 실패를 말하지 않는다 — 뼈대와 실패 알림이 한 화면에 서지 않게
  const { notice: feedNotice } = runHistoryView({ rows: activities?.length ?? null, failed: feedError, refreshing: loading });
  return (
    <Paper
      elevation={0}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Recent Activity
        </Typography>
        {activities !== null && activities.length > 0 && (
          <Chip
            label={activities.length}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>
      {/* 피드를 모르거나(조회 실패) 최신이 아닐 때의 말은 정본 판정 — 조회 중은 뼈대(스켈레톤)로 보인다 */}
      {(feedNotice === 'failed' || feedNotice === 'stale') && <RunHistoryNotice state={feedNotice} sx={{ px: 2, py: 1 }} />}
      {feedNotice === 'failed' ? null : loading || activities === null ? (
        <Box sx={{ px: 2, py: 1 }}>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Box>
      ) : activities.length === 0 ? (
        <Box sx={{ px: 2, py: 2 }}>
          {/* 프로젝트 목록을 읽지 못하면 운전 템플릿을 거르지 못해 빈 피드를 «기록 없음» 으로 단정하지 않는다 */}
          {loadFailed ? (
            <RunHistoryNotice state="failed" sx={{ py: 0 }} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {`${RUN_HISTORY_EMPTY_TEXT} — 프로젝트를 열어 SIMULATION을 시작하면 최근 실행이 여기에 표시됩니다.`}
            </Typography>
          )}
        </Box>
      ) : (
        <List dense sx={{ py: 0 }}>
          {activities.map((entry, index) => (
            <React.Fragment key={`${entry.projectId}-${entry.id}`}>
              <ListItemButton
                component="li"
                sx={{ py: 1, pr: 1, minWidth: 0 }}
                onClick={() => handleActivityClick(entry)}
              >
                {/* 아이콘만으로 전달되던 상태를 보조기술에도 노출 */}
                <ListItemIcon
                  sx={{ minWidth: 28 }}
                  role="img"
                  aria-label={runStatusLabel(entry.status)}
                >
                  <RunStatusIcon status={entry.status} fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {runEntryName(entry)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, ml: 1 }}
                      >
                        {formatRelative(entry.timestamp)}
                      </Typography>
                    </Box>
                  }
                  secondary={entry.projectName}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { color: 'text.secondary' },
                    noWrap: true,
                  }}
                  sx={{ my: 0, minWidth: 0 }}
                />
              </ListItemButton>
              {index < activities.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default RecentActivityPanel;
